import React from 'react';
import {
  ATMDivider,
  ATMForm,
  ATMGrid,
  ATMHeader,
  ATMSegment,
  ATMTable,
} from 'shared-it-appmod-ui';
import FieldReadOnlyCommment from 'src/components/atoms/field/field-readonly-comment/field-readonly-comment.component';
import FieldReadOnly from 'src/components/atoms/field/field-readonly/field-readonly.component';
import FlagReadOnly from 'src/components/atoms/flag-readonly/flag-readonly.component';
import FileManager from 'src/components/molecules/file-manager/file-manager.component';
import { FileCategory } from 'src/constants';
import Lang from 'src/libraries/language';
import { IRequest } from 'src/models/request.model';
import FlightRequestConstructionLandingZone from '../flight-request-construction-landing-zone/flight-request-construction-landing-zone.component';
import FlightRequestContactInformation from '../flight-request-contact-information/flight-request-contact-information.component';
import styles from './flight-request-construction-contractor.module.scss';

type IProps = {
  data: IRequest;
};

const FlightRequestConstructionContractor: React.FC<IProps> = ({
  data: { constructionContractorFlight },
}) => {
  return (
    <ATMSegment color="purple" className={styles.wrapper}>
      <ATMHeader>{Lang.TTL_REQUEST_CONSTRUCTION_CONTRACTOR}</ATMHeader>

      <ATMForm>
        <ATMGrid columns={2}>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_PROJECT}>
              {constructionContractorFlight.projectTitle}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_PIPELINE_NUMBER}>
              {constructionContractorFlight.circuit}
            </FieldReadOnly>
          </ATMGrid.Column>
        </ATMGrid>
        {constructionContractorFlight.purpose && (
          <ATMGrid columns={2}>
            <ATMGrid.Column>
              <FieldReadOnly
                label={Lang.LBL_PURPOSE}
                className={styles.wrapText}
              >
                {constructionContractorFlight.purpose}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid>
        )}
        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_STRUCTURE_IDS}
        </ATMHeader>

        <ATMTable>
          <ATMTable.Header>
            <ATMTable.Row>
              <ATMTable.HeaderCell>{Lang.LBL_STRUCTURE_ID}</ATMTable.HeaderCell>
            </ATMTable.Row>
          </ATMTable.Header>

          <ATMTable.Body>
            {(constructionContractorFlight.structureInfos ?? []).map(
              (value, key) => (
                <ATMTable.Row key={`structure_${key}`}>
                  <ATMTable.Cell>{value.structureId}</ATMTable.Cell>
                </ATMTable.Row>
              )
            )}
          </ATMTable.Body>
        </ATMTable>

        <ATMForm.Field>
          <label>{Lang.NTE_ATTACH_FILE_ASSOCIATED_WITH_STRUCTURES}</label>
          <FileManager
            files={constructionContractorFlight.structureFiles}
            allowedTypes={[FileCategory.Structure]}
            editable={false}
            inline
          />
        </ATMForm.Field>

        <FlightRequestConstructionLandingZone
          data={constructionContractorFlight.landingInfos}
        />

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.nightFlag}
              label={Lang.LBL_NIGHT_FLIGHT}
            />
          </ATMGrid.Column>

          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.sensitiveFlag}
              label={Lang.LBL_SENSITIVE_CUSTOMER}
            />

            {constructionContractorFlight.sensitiveFlag &&
              (constructionContractorFlight?.sensitiveFiles ?? []).length >
                0 && (
                <FileManager
                  files={constructionContractorFlight?.sensitiveFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.culturalFlag}
              label={Lang.LBL_CULTURAL_RESTRICTION}
            />

            {constructionContractorFlight.culturalFlag &&
              (constructionContractorFlight?.culturalFiles ?? []).length >
                0 && (
                <FileManager
                  files={constructionContractorFlight?.culturalFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>
        </ATMGrid>

        <FlightRequestContactInformation data={constructionContractorFlight} />

        <ATMHeader size="small" className="text-underline">
          {Lang.TTL_PLAN_OPERATIONS}
        </ATMHeader>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.liftFlag}
              label={Lang.LBL_LIFT_PLAN}
            />

            {constructionContractorFlight.liftFlag &&
              (constructionContractorFlight?.liftFiles ?? []).length > 0 && (
                <FileManager
                  files={constructionContractorFlight?.liftFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.complianceFlag}
              label={Lang.LBL_SDGE_AOM_REVIEW_OPERATOR_COMPLIANCE}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.congestedFlag}
              label={Lang.LBL_CONGESTED_AREA_PLAN}
            />

            {constructionContractorFlight.congestedFlag &&
              (constructionContractorFlight?.congestedFiles ?? []).length >
                0 && (
                <FileManager
                  files={constructionContractorFlight?.congestedFiles}
                  inline
                  editable={false}
                />
              )}
          </ATMGrid.Column>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.TTL_WORK_SCOPE}
        </ATMHeader>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.lineFlag}
              label={Lang.LBL_LONG_LINE}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.sockFlag}
              label={Lang.LBL_SOCK_LINE_PULL}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.platformFlag}
              label={Lang.LBL_PLATFORM_WORK}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.skidFlag}
              label={Lang.LBL_SKID_TRANSFER}
            />
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.hecFlag}
              label={Lang.LBL_SHORT_HAUL_HEC}
            />
            {constructionContractorFlight.hecFlag && (
              <FileManager
                files={constructionContractorFlight?.hecFiles}
                inline
                editable={false}
              />
            )}
          </ATMGrid.Column>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.TTL_AIRCRAFT_INFORMATION}
        </ATMHeader>

        <ATMGrid columns={4}>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_AIRCRAFT_TYPE}>
              {constructionContractorFlight.aircraftType}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FieldReadOnly label={Lang.LBL_TAIL_NUMBER}>
              {constructionContractorFlight.tailNo}
            </FieldReadOnly>
          </ATMGrid.Column>
          <ATMGrid.Column>
            <FlagReadOnly
              value={constructionContractorFlight.trackerFlag}
              label={Lang.LBL_SDGE_TRACKER}
            />
          </ATMGrid.Column>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_FLIGHT_AREA}
        </ATMHeader>
        <ATMForm.Field>
          <FileManager
            files={constructionContractorFlight.flightAreaFiles}
            editable={false}
            inline
          />
        </ATMForm.Field>
        <ATMDivider />

        <ATMHeader size="small" className="text-underline">
          {Lang.TTL_BRIEF_DESCRIPTION}
        </ATMHeader>

        <FieldReadOnlyCommment>
          {constructionContractorFlight.comments}
        </FieldReadOnlyCommment>
      </ATMForm>
    </ATMSegment>
  );
};

export default FlightRequestConstructionContractor;
